import { PlusIcon } from '@phosphor-icons/react';
import { formatDistanceToNow } from 'date-fns';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useProjectConversations } from '@/hooks/chat/useProjectConversations';
import { trackProductEvent } from '@/lib/analytics/product-events';
import { buildProjectChatUrl } from '@/lib/plans/project-chat-url';
import {
  ICON_BUTTON,
  ITEM_META,
  ITEM_TITLE,
  MOBILE_HIT_AREA,
  ROW_HOVER,
  SECTION_TITLE,
} from '@/lib/ui-tokens';
import { cn } from '@/lib/utils';

/**
 * Compact right-rail list of conversations attached to this project.
 * Two rows max preview; "See all N" inline-expands the rest.
 *
 * Starting a fresh chat lives in the page header ("New chat" action on
 * desktop, floating button on the mobile hub).
 * This component is rows-only; clicking a row resumes that session in the
 * home composer with the project bound. The `+` icon-button mirrors the
 * Files section convention — fast affordance for "fresh chat against this
 * project" without needing to retype a prompt in the composer.
 */

const PREVIEW_COUNT = 2;

interface ProjectConversationsListProps {
  projectId: string;
  /**
   * Rendered when the project has zero conversations. Defaults to `null` so
   * the right rail simply omits the section — but the mobile Chats tab passes
   * an empty state so the panel isn't a blank void.
   */
  emptyState?: React.ReactNode;
}

export const ProjectConversationsList: React.FC<ProjectConversationsListProps> = ({
  projectId,
  emptyState = null,
}) => {
  const navigate = useNavigate();
  const { conversations, totalCount } = useProjectConversations(projectId, { limit: 20 });
  const [showAll, setShowAll] = useState(false);

  if (conversations.length === 0) return <>{emptyState}</>;

  const visible = showAll ? conversations : conversations.slice(0, PREVIEW_COUNT);
  const hasOverflow = conversations.length > PREVIEW_COUNT;

  const resume = (sessionId: string, workspaceId: string | null) => {
    void trackProductEvent('project_chat_strip_resume_clicked', {
      project_id: projectId,
      session_id: sessionId,
    });
    // Explicit conversation target — the home composer honors `resumeSession`
    // alongside `planId` and opens exactly this thread (not the newest).
    navigate(buildProjectChatUrl({ planId: projectId, resumeSessionId: sessionId, workspaceId }));
  };

  const startNew = () => {
    void trackProductEvent('project_new_chat_clicked', { project_id: projectId });
    navigate(buildProjectChatUrl({ planId: projectId, isNew: true }));
  };

  return (
    <section className="p-5">
      <div className="mb-2 flex items-center justify-between">
        <h3 className={cn(SECTION_TITLE, 'flex items-baseline gap-1.5')}>
          <span>Conversations</span>
          <span className="text-detail text-muted-foreground/60 tabular-nums font-normal">
            {totalCount}
          </span>
        </h3>
        <button
          type="button"
          onClick={startNew}
          className={cn(ICON_BUTTON, MOBILE_HIT_AREA)}
          aria-label="Start new conversation in this project"
        >
          <PlusIcon className="size-3.5" />
        </button>
      </div>
      {/* Quiet List contract: spacing separates rows, never divider lines
          (matches ChatGPT's project-conversation list 1:1). */}
      <div className="flex flex-col gap-0.5">
        {visible.map((c) => (
          <button
            key={c.id}
            type="button"
            onClick={() => resume(c.id, c.workspaceId)}
            className={cn(
              'group flex w-full flex-col gap-0.5 py-2 px-2 -mx-2 rounded-sm text-left',
              ROW_HOVER,
            )}
          >
            <p className={cn(ITEM_TITLE, 'truncate')}>{c.title}</p>
            <div className={cn('flex items-center gap-1.5', ITEM_META)}>
              <span>{formatDistanceToNow(new Date(c.updatedAt), { addSuffix: true })}</span>
            </div>
          </button>
        ))}
      </div>
      {hasOverflow && (
        <button
          type="button"
          onClick={() => setShowAll((v) => !v)}
          className="mt-1.5 text-detail text-foreground/50 hover:text-foreground/70 transition-colors"
        >
          {showAll ? 'Show fewer' : `See all ${totalCount} →`}
        </button>
      )}
    </section>
  );
};
